import { Link, useNavigate } from "@tanstack/react-router";
import {
  BookOpenIcon,
  CalendarClockIcon,
  EllipsisIcon,
  FileUpIcon,
  MessageSquareTextIcon,
  RefreshCwIcon,
  SearchIcon,
} from "lucide-react";
import { cn } from "~/lib/utils";
import { Badge } from "../../ui/badge";
import { Button } from "../../ui/button";
import { Menu, MenuItem, MenuPopup, MenuTrigger } from "../../ui/menu";
import { TableCell, TableRow } from "../../ui/table";
import { InspectionStatusBadge } from "./InspectionStatus";
import {
  formatDuration,
  inspectionActive,
  inspectionTitle,
  inspectionStatusCopy,
  inspectionKind,
  TRIGGER_LABEL,
  type InspectionKind,
  type InspectionRun,
  type InspectionTrigger,
} from "./inspectorView";
import { reassessmentChangeLabel } from "../policy/PolicyReassessment.logic";

const day = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short" });
const time = (iso: string) =>
  new Date(iso).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });

const KIND_ICON: Record<InspectionTrigger, typeof SearchIcon> = {
  daily: CalendarClockIcon,
  weekly: CalendarClockIcon,
  upload: FileUpIcon,
  manual: BookOpenIcon,
  finding: MessageSquareTextIcon,
  // A re-reading of what already stands, not a new look at the document.
  reassessment: RefreshCwIcon,
};

/** Each kind of run in its own colour: a review of one finding, a review of the whole library, the machine's runs. */
const KIND_BADGE: Record<InspectionKind, "info" | "warning" | "secondary"> = {
  finding: "info",
  library: "warning",
  sweep: "secondary",
  upload: "secondary",
  reassessment: "info",
};

/** "1 finding, 2 suggestions" — what the run wrote into the library, or nothing. */
export function recordedSummary(found: InspectionRun["found"]): string {
  const recorded = [
    [found.findings, "finding", "findings"],
    [found.suggestions, "suggestion", "suggestions"],
    [found.publications, "publication", "publications"],
    [found.revised, "revision", "revisions"],
    [found.withdrawn, "withdrawal", "withdrawals"],
  ] as const;
  return recorded
    .filter(([n]) => n > 0)
    .map(([n, one, many]) => `${n} ${n === 1 ? one : many}`)
    .join(", ");
}

/**
 * One run as a data-table row, after shadcn's dashboard table: the title,
 * its type as an outline badge, one line of what came of it (the server
 * strips the report's heading and markdown), what it recorded, status,
 * when it started, how long it took, and a row menu. The whole row opens
 * the run; the title is the link for the keyboard.
 */
export function InspectionRunRow({ run, orgSegment }: { run: InspectionRun; orgSegment: string }) {
  const navigate = useNavigate();
  const title = inspectionTitle(run);
  const kind = run.trigger ? TRIGGER_LABEL[run.trigger] : "Inspection";
  const KindIcon = run.trigger ? KIND_ICON[run.trigger] : SearchIcon;
  const recorded = recordedSummary(run.found);
  const active = inspectionActive(run);
  const open = () =>
    void navigate({
      to: "/$org/compliance/inspector/$threadId",
      params: { org: orgSegment, threadId: run.threadId },
    });
  const copyLink = () => {
    const href = `${window.location.origin}/${orgSegment}/compliance/inspector/${run.threadId}`;
    void navigator.clipboard?.writeText(href);
  };
  return (
    <TableRow
      data-testid="inspection-run-row"
      data-active={active || undefined}
      className="cursor-pointer align-top"
      onClick={open}
    >
      <TableCell className="py-2.5 whitespace-normal">
        <Link
          to="/$org/compliance/inspector/$threadId"
          params={{ org: orgSegment, threadId: run.threadId }}
          className="line-clamp-2 break-words text-sm font-medium leading-5 text-foreground outline-none focus-visible:underline"
          onClick={(event) => event.stopPropagation()}
        >
          {title}
        </Link>
        {run.reassessment ? (
          // Which change this run re-reads; several documents can be re-assessed on one day.
          <span className="mt-0.5 block text-xs text-muted-foreground">
            {reassessmentChangeLabel(run.reassessment)}
          </span>
        ) : null}
      </TableCell>
      <TableCell className="py-2.5">
        <Badge variant={KIND_BADGE[inspectionKind(run)]} className="gap-1 px-1.5">
          <KindIcon aria-hidden className="size-3" />
          {kind}
        </Badge>
      </TableCell>
      <TableCell className="py-2.5 whitespace-normal">
        <span
          className={cn(
            "line-clamp-2 break-words text-xs leading-5",
            run.status === "failed" ? "text-destructive" : "text-muted-foreground",
          )}
        >
          {run.status === "failed"
            ? inspectionStatusCopy(run)
            : run.summary || inspectionStatusCopy(run)}
        </span>
      </TableCell>
      <TableCell className="py-2.5 whitespace-normal text-xs leading-5 text-muted-foreground">
        {recorded || <span aria-label="Nothing recorded">—</span>}
      </TableCell>
      <TableCell className="py-2.5">
        <InspectionStatusBadge status={run.status} />
      </TableCell>
      <TableCell className="py-2.5 text-right text-xs leading-5 tabular-nums text-muted-foreground">
        <time dateTime={run.startedAt} className="block text-foreground">
          {day(run.startedAt)}, {time(run.startedAt)}
        </time>
        <span className="block">
          {run.completedAt
            ? formatDuration(run.startedAt, run.completedAt)
            : active
              ? "running"
              : "—"}
        </span>
      </TableCell>
      <TableCell className="py-1.5 pr-2 text-right" onClick={(event) => event.stopPropagation()}>
        <Menu>
          <MenuTrigger
            render={
              <Button
                variant="ghost"
                size="icon-sm"
                className="size-7 text-muted-foreground data-[popup-open]:bg-muted"
                aria-label={`Actions for ${title}`}
              />
            }
          >
            <EllipsisIcon aria-hidden className="size-4" />
          </MenuTrigger>
          <MenuPopup align="end" className="w-36">
            <MenuItem onClick={open}>Open run</MenuItem>
            <MenuItem onClick={copyLink}>Copy link</MenuItem>
          </MenuPopup>
        </Menu>
      </TableCell>
    </TableRow>
  );
}
